import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Alert, Button, Empty, Modal, Spin, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ApplicationDefinitionConditionalEditStore } from '../../stores';
import { AdditionalClassificationDto, AdditionalClassificationOptionDto } from '../../types';
import './AddAdditionalClassificationModal.less';

interface Props {
    store: ApplicationDefinitionConditionalEditStore;
}

type StructureBadge = 'Standalone' | 'Has Dependent Classifications' | 'Depends on standard Classification';

type PreviewPropertyRow = {
    key: string;
    property: string;
    value: React.ReactNode;
};

const optionColumns: ColumnsType<AdditionalClassificationOptionDto> = [
    {
        title: 'Classification',
        dataIndex: 'displayName',
        key: 'displayName',
        render: (_value, record) => (
            <div className="additional-classification-modal__classification">
                <div className="group-title">{record.displayName}</div>
                <div className="group-subtitle">SICS Slot: {record.classificationSlot}</div>
            </div>
        )
    },
    {
        title: 'ActiveValues',
        dataIndex: 'activeValueCount',
        key: 'activeValueCount',
        width: 140
    }
];

const AddAdditionalClassificationModal: React.FC<Props> = ({ store }) => {
    const selectedRowKeys = store.selectedAdditionalClassification
        ? [store.selectedAdditionalClassification.classificationSlot]
        : [];
    const isPreviewStep = store.additionalClassificationModalStep === 'preview';
    const showEmptyOptions =
        !store.isLoadingAdditionalClassifications && store.additionalClassificationOptions.length === 0;

    const countIncludedFields = (classification: AdditionalClassificationDto): number =>
        1 + (classification.children ?? []).reduce((count, child) => count + countIncludedFields(child), 0);

    const getStructure = (classification: AdditionalClassificationDto): StructureBadge =>
        classification.children?.length ? 'Has Dependent Classifications' : 'Standalone';

    const getLeafDependencyPaths = (
        classification: AdditionalClassificationDto,
        path: string[] = []
    ): string[] => {
        const nextPath = [...path, classification.displayName];

        if (!classification.children?.length) {
            return path.length > 0 ? [nextPath.join(' -> ')] : [];
        }

        return classification.children.reduce<string[]>(
            (paths, child) => [...paths, ...getLeafDependencyPaths(child, nextPath)],
            []
        );
    };

    const getDependencyPaths = (classification: AdditionalClassificationDto, structure: StructureBadge): string[] => {
        if (structure === 'Standalone') {
            return [];
        }

        return getLeafDependencyPaths(classification);
    };

    const renderStructureValue = (classification: AdditionalClassificationDto) => {
        const structure = getStructure(classification);
        const dependencyPaths = getDependencyPaths(classification, structure);

        return (
            <div className="additional-classification-modal__structure">
                <Tag color={getStructureBadgeColor(structure)}>{structure}</Tag>
                {dependencyPaths.length ? (
                    <div className="additional-classification-modal__dependency-paths">
                        {dependencyPaths.map(path => (
                            <div key={path} className="additional-classification-modal__dependency-path">
                                {path}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        );
    };

    const getStructureBadgeColor = (structure: StructureBadge) => {
        switch (structure) {
            case 'Has Dependent Classifications':
                return 'blue';
            case 'Depends on standard Classification':
                return 'gold';
            default:
                return 'default';
        }
    };

    const previewColumns: ColumnsType<PreviewPropertyRow> = [
        {
            title: '',
            dataIndex: 'property',
            key: 'property',
            width: 180,
            render: (_value, record) => (
                <span className="additional-classification-modal__property-name">{record.property}</span>
            )
        },
        {
            title: '',
            dataIndex: 'value',
            key: 'value'
        }
    ];

    const previewRows: PreviewPropertyRow[] = store.previewAdditionalClassification
        ? [
              {
                  key: 'classification',
                  property: 'Classification',
                  value: store.previewAdditionalClassification.displayName
              },
              {
                  key: 'classificationSlot',
                  property: 'SICS Slot',
                  value: store.previewAdditionalClassification.classificationSlot
              },
              {
                  key: 'activeValueCount',
                  property: 'Active Values',
                  value: store.previewAdditionalClassification.activeValueCount ?? '-'
              },
              {
                  key: 'structure',
                  property: 'Structure',
                  value: renderStructureValue(store.previewAdditionalClassification)
              },
              {
                  key: 'includedFields',
                  property: 'Included fields',
                  value: countIncludedFields(store.previewAdditionalClassification)
              }
          ]
        : [];

    return (
        <Modal
            title="New Classification"
            className="alpha-portal-dialog"
            visible={store.addAdditionalClassificationModalVisible}
            onCancel={store.closeAddAdditionalClassificationModal}
            maskClosable={false}
            closable={false}
            destroyOnClose
            width={640}
            centered
            footer={[
                isPreviewStep && (
                    <Button
                        data-id="add-additional-classification-back"
                        className="light"
                        key="back"
                        size="large"
                        onClick={store.handleAdditionalClassificationBack}
                    >
                        Back
                    </Button>
                ),
                <Button
                    data-id="add-additional-classification-cancel"
                    className="light"
                    key="cancel"
                    size="large"
                    onClick={store.closeAddAdditionalClassificationModal}
                >
                    Cancel
                </Button>,
                <Button
                    data-id="add-additional-classification-next"
                    key="next"
                    size="large"
                    type="primary"
                    onClick={
                        isPreviewStep
                            ? store.confirmAdditionalClassification
                            : store.handleAdditionalClassificationNext
                    }
                    loading={store.isLoadingAdditionalClassificationPreview}
                    disabled={!store.selectedAdditionalClassification || store.isLoadingAdditionalClassificationPreview}
                >
                    {isPreviewStep ? 'Add Classification' : 'Next'}
                </Button>
            ]}
        >
            {store.additionalClassificationsError && (
                <Alert
                    type="error"
                    message={store.additionalClassificationsError}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {store.additionalClassificationsPreviewError && (
                <Alert
                    type="error"
                    message={store.additionalClassificationsPreviewError}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {isPreviewStep ? (
                <Spin spinning={store.isLoadingAdditionalClassificationPreview}>
                    {store.previewAdditionalClassification ? (
                        <Table<PreviewPropertyRow>
                            data-id="additional-classification-preview-table"
                            rowKey="key"
                            bordered={false}
                            className="alpha-portal-table additional-classification-modal__table additional-classification-modal__preview-table"
                            columns={previewColumns}
                            dataSource={previewRows}
                            pagination={false}
                            showHeader={false}
                        />
                    ) : (
                        <Empty description="No dependency preview available" />
                    )}
                </Spin>
            ) : showEmptyOptions ? (
                <Empty description="No available additional classifications" />
            ) : (
                <Table<AdditionalClassificationOptionDto>
                    data-id="additional-classifications-options-table"
                    rowKey="classificationSlot"
                    bordered={false}
                    className="alpha-portal-table additional-classification-modal__table"
                    columns={optionColumns}
                    dataSource={store.additionalClassificationOptions}
                    loading={store.isLoadingAdditionalClassifications}
                    pagination={false}
                    rowSelection={{
                        type: 'radio',
                        selectedRowKeys,
                        onChange: (_selectedRowKeys, selectedRows) => {
                            store.setSelectedAdditionalClassification(selectedRows[0]);
                        }
                    }}
                    onRow={record => ({
                        onClick: () => store.setSelectedAdditionalClassification(record)
                    })}
                />
            )}
        </Modal>
    );
};

export default observer(AddAdditionalClassificationModal);
